import { Category } from "@prisma/client";
import { FC } from "react";
import { GetServerSideProps } from "next";
import { Menu } from "@headlessui/react";
import { useRouter } from "next/router";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";

import SupplierCard from "@components/common/supplier-card";
import FilterMenu from "@components/products/filters/filter-menu";
import Layout from "@components/common/layout";
import { StaticProps } from "@interfaces/page";
import prisma, { getStaticData } from "@lib/prisma";
import TitleDescriptionHeader from "@components/common/title-description-header";
import { classNames } from "@utils/class-names";
import {
  supplierWithCategories,
  SupplierWithCategories,
} from "@interfaces/supplier";

interface Props extends StaticProps {
  suppliers: SupplierWithCategories[];
  categories: Category[];
}

const Partners: FC<Props> = ({ suppliers, categories }) => {
  const router = useRouter();
  const categoryQuery = router.query.category;
  const isCategorySet = !!categoryQuery && typeof categoryQuery === "string";
  const category = isCategorySet
    ? categories.find((c) => c.slug === categoryQuery)?.name || null
    : null;
  const { t } = useTranslation();

  const filteredSuppliers = suppliers.filter(
    ({ supplierToCategory }) =>
      !isCategorySet ||
      supplierToCategory.some(
        ({ categorySlug }) => !isCategorySet || categorySlug === categoryQuery
      )
  );

  const title = t("partners.title");
  const description = t("partners.description");

  return (
    <Layout {...{ title, description }}>
      <div className="pt-2">
        <TitleDescriptionHeader {...{ title, description }} />
        <div className="flex justify-center items-center">
          <FilterMenu
            {...{
              text: `${t("partners.category")}: ${
                category ? category : t("partners.all")
              }`,
              className: "flex-col w-48 divide-y-2 divide-gray-400 py-1 z-30",
            }}
          >
            <Menu.Item disabled={!category}>
              {({ active, disabled }) => (
                <button
                  className={classNames(
                    "text-left py-2 px-4 w-full transition-all duration-200",
                    active ? "bg-gray-400" : "",
                    disabled ? "text-gray-400" : ""
                  )}
                  onClick={() => router.push({ pathname: "/partners" })}
                >
                  {router.locale === "en" ? "All" : "Sve"}
                </button>
              )}
            </Menu.Item>
            {categories.map(({ slug, name, nameEn }) => (
              <Menu.Item key={slug} disabled={category === name}>
                {({ active, disabled }) => (
                  <button
                    className={classNames(
                      "text-left py-2 px-4 w-full transition-all duration-200",
                      active ? "bg-gray-400" : "",
                      disabled ? "text-gray-400" : ""
                    )}
                    onClick={() => {
                      router.push({
                        pathname: "/partners",
                        query: { category: slug },
                      });
                    }}
                  >
                    {router.locale === "en" ? nameEn : name}
                  </button>
                )}
              </Menu.Item>
            ))}
          </FilterMenu>
        </div>
        <div className="flex flex-wrap px-10 w-full justify-center items-center">
          {filteredSuppliers.map((supplier) => (
            <div
              key={supplier.name}
              className="w-full sm:w-1/2 md:w-1/4 lg:w-1/6"
            >
              <SupplierCard {...supplier} />
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default Partners;

export const getServerSideProps: GetServerSideProps<Props> = async ({
  locale,
}) => {
  const [suppliers, categories, staticData] = await getStaticData([
    prisma.supplier.findMany({
      ...supplierWithCategories,
      where: { isOnLandingPage: true },
    }),
    prisma.category.findMany({ where: { isMain: true } }),
  ]);

  const translations = await serverSideTranslations(locale as string);

  return {
    props: {
      suppliers,
      categories,
      staticData,
      ...translations,
    },
  };
};
